// F-2 · PlanDetailPage — /construction-plans/:id (fills the dead View link
// from ConstructionPlansPage, blueprint v2 Phase F).
//   v2 plans  → render with LiftDetailCard list. Read-only UNLESS the plan is
//     an unsigned draft (D16 sign-off semantics: sign-off freezes; a
//     post-signoff edit bumps version+1).
//   v1 plans  → read-only in today's layout + 'UNCLASSIFIED (legacy)' badge +
//     one-click 'Upgrade to v2' → planApi.upgradeToV2 → re-render as v2.
import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Lock, Pencil, Save, Download } from 'lucide-react';
import { api } from '@/utils/api';
import { getPlan, updatePlan, upgradeToV2, planApiErrorMessage, type ConstructionPlanRecord } from '@/utils/planApi';
import { readPlan, isV1Plan } from '@/utils/planDataCodec';
import { recomputeComputed } from '@/utils/recomputeLift';
import { LiftDetailCard } from '@/components/rigging/LiftDetailCard';
import { ComplianceSteps } from '@/components/classification/ComplianceSteps';
import { planComplianceStatus } from '@/utils/planComplianceStatus';
import type { PlanDataV2, LiftDetail, EquipmentInspectionSnapshotEntry } from '@/types/planData';

interface LinkedJha {
  id: number;
  title?: string | null;
  signedAt?: string | null;
}

export function PlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<ConstructionPlanRecord | null>(null);
  const [planData, setPlanData] = useState<PlanDataV2 | null>(null);
  const [linkedJhas, setLinkedJhas] = useState<LinkedJha[]>([]);
  const [legacy, setLegacy] = useState(false);
  const [legacyData, setLegacyData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [editMode, setEditMode] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const rec = await getPlan(parseInt(id, 10));
      setRecord(rec);
      // DE-006: JHAs linked to this plan (constructionPlanId back-link).
      setLinkedJhas(
        (Array.isArray(rec.linkedJhas) ? (rec.linkedJhas as LinkedJha[]) : [])
      );
      const raw = rec.planData;
      const rawRecord = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
      if (isV1Plan({ planData: raw })) {
        setLegacy(true);
        setLegacyData(rawRecord);
        setPlanData(null);
      } else {
        setLegacy(false);
        setLegacyData(null);
        setPlanData(readPlan({ planData: raw, createdAt: rec.createdAt }));
      }
    } catch (error) {
      setToast({ kind: 'error', message: planApiErrorMessage(error, 'Failed to load construction plan.') });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // D16: signed plans are frozen — read-only. Edits after sign-off bump version+1.
  const signed = !!record?.signedAt;
  const canEdit = !legacy && !signed && editMode;

  const handleUpgrade = async () => {
    if (!record) return;
    setUpgrading(true);
    try {
      await upgradeToV2(record.id);
      setToast({ kind: 'success', message: 'Plan upgraded to schema v2.' });
      await load();
    } catch (error) {
      setToast({ kind: 'error', message: planApiErrorMessage(error, 'Failed to upgrade the plan to v2.') });
    } finally {
      setUpgrading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!record || !planData) return;
    setSaving(true);
    try {
      // Audit #4 (HIGH): strip the `attachments` key — this tab's mirror may be
      // stale, and the server merge would use it as-is, resurrecting entries
      // another tab deleted. Upload/delete routes are the ONLY writers of
      // planData.attachments (mirrors buildPlanPayload's deliberate omission).
      const { attachments: _omit, ...rest } = planData as unknown as Record<string, unknown>;
      void _omit;
      const editPayload = rest as Record<string, unknown>;
      const saved = await updatePlan(record.id, {
        planData: editPayload,
        editSummary: 'Edit from plan detail page (post-sign-off — new version)',
      });
      setRecord(saved);
      setEditMode(false);
      setToast({ kind: 'success', message: `Saved (version ${saved.version}).` });
      await load();
    } catch (error) {
      setToast({ kind: 'error', message: planApiErrorMessage(error, 'Failed to save the plan.') });
    } finally {
      setSaving(false);
    }
  };

  const updateLift = (index: number, next: LiftDetail) => {
    if (!planData) return;
    setPlanData({
      ...planData,
      liftDetails: planData.liftDetails.map((l, i) => (i === index ? next : l)),
    });
  };

  // S5 (F2): download a linked project document via the existing token-auth
  // endpoint (same blob-fetch pattern as ProjectsPage.downloadDocument).
  const handleDownloadLinkedDoc = useCallback(
    async (doc: { id: number; originalName: string }) => {
      if (!record) return;
      try {
        const res = await api.get(`/projects/${record.projectId}/documents/${doc.id}/download`, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.originalName || 'document';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading linked document:', error);
        setToast({ kind: 'error', message: 'Failed to download linked document.' });
      }
    },
    [record]
  );

  const linkedDocuments = record?.linkedDocuments ?? [];

  const headerClass = planData?.classification?.selected ?? null;

  // UAT-015: the point-in-time inspection snapshot captured at save, keyed
  // by asset id — the saved plan keeps the inspection status it knew.
  const verdictSnapshot: Record<string, EquipmentInspectionSnapshotEntry> = Object.fromEntries(
    (planData?.equipmentInspections ?? []).map((e) => [String(e.assetId), e])
  );
  // DE-006: the plan's JHA linkage — the stored reference, or the real
  // constructionPlanId back-link when the payload has no explicit reference.
  const effectiveJhaReference =
    planData?.jhaReference ?? (linkedJhas.length > 0 ? `jha-${linkedJhas[0].id}` : null);
  // UAT-013: state-aware compliance banner — compliant ONLY when classified,
  // all compliance steps resolved, AND signed off.
  const compliance = planData
    ? planComplianceStatus({
        classification: planData.classification,
        signed,
        steps: planData.complianceSteps ?? null,
      })
    : null;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="container mx-auto px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/construction-plans" className="text-gray-600 hover:text-gray-900 flex-shrink-0" aria-label="Back to plans">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900">
                {legacy ? (
                  <>
                    UNCLASSIFIED (legacy)
                    <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full align-middle">
                      LEGACY v1
                    </span>
                  </>
                ) : headerClass ? (
                  `CLASS ${headerClass} CONSTRUCTION PLAN`
                ) : (
                  'UNCLASSIFIED CONSTRUCTION PLAN'
                )}
              </h1>
              <p className="text-sm text-gray-600">
                {record?.title ?? ''}
                {record ? ` — version ${record.version}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {legacy && (
              <button
                onClick={handleUpgrade}
                disabled={upgrading}
                data-testid="upgrade-to-v2"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${upgrading ? 'animate-spin' : ''}`} />
                {upgrading ? 'Upgrading…' : 'Upgrade to v2'}
              </button>
            )}
            {!legacy && signed && (
              <span className="flex items-center gap-1 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-semibold">
                <Lock className="h-4 w-4" /> Signed — read-only
              </span>
            )}
            {!legacy && !signed && !editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 flex items-center gap-2"
              >
                <Pencil className="h-4 w-4" /> Edit
              </button>
            )}
            {!legacy && !signed && editMode && (
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-gap-2"
              >
                <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {toast && (
          <div
            role="status"
            className={`fixed bottom-6 right-6 z-50 max-w-md px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.kind === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}
          >
            {toast.message}
          </div>
        )}
        {loading && <p className="text-gray-500">Loading plan…</p>}

        {/* LEGACY v1 — read-only in today's layout + upgrade button */}
        {!loading && legacy && legacyData && (
          <div data-testid="legacy-plan-view" className="space-y-6">
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900" data-testid="legacy-readonly-banner">
              Legacy (v1) plan — read-only. Upgrade to v2 to edit, recompute forces, and classify.
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="font-bold text-gray-900 border-b pb-2 mb-4">STORED PLAN DATA (v1 — keys verbatim)</h2>
              <pre className="text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(legacyData, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* v2 — LiftDetailCard list (computed recomputed on every load, never from storage) */}
        {!loading && !legacy && planData && (
          <div data-testid="v2-plan-view" className="space-y-4">
            {!signed && !editMode && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
              Viewing draft plan — click Edit to modify. Sign-off freezes the plan; a post-sign-off edit creates version +1 (D16).
            </div>
            )}
            {compliance && (
              <div
                data-testid="plan-compliance-status"
                className={`rounded-lg p-3 text-sm border ${
                  compliance.state === 'compliant'
                    ? 'bg-green-50 border-green-300 text-green-900'
                    : 'bg-amber-50 border-amber-300 text-amber-900'
                }`}
              >
                {compliance.state === 'compliant' ? (
                  <><strong>ANSI/ASSP A10.48-2023</strong> Compliant Construction Plan — classified (CLASS {headerClass}), all compliance steps resolved, and signed off.</>
                ) : (
                  <><strong>{compliance.label}.</strong> {compliance.detail}</>
                )}
              </div>
            )}
            {planData.liftDetails.map((lift, i) => (
              <LiftDetailCard
                key={lift.id}
                lift={lift}
                index={i}
                computed={recomputeComputed(lift)}
                onChange={(next) => updateLift(i, next)}
                readOnly={!canEdit}
                canRemove={canEdit && planData.liftDetails.length > 1}
                anchorVehicle={planData.anchorVehicle ?? null}
                verdictSnapshot={verdictSnapshot}
                onRemove={() =>
                  setPlanData({
                    ...planData,
                    liftDetails: planData.liftDetails.filter((_, j) => j !== i),
                  })
                }
              />
            ))}
            {/* DE-006: the real JHA linkage — JHAs back-linked to this plan via
                constructionPlanId (auto-linked on create, or manual). */}
            {linkedJhas.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6" data-testid="linked-jhas">
                <h2 className="font-bold text-gray-900 border-b pb-2 mb-4">LINKED JHA (§1.6 rule 3)</h2>
                <ul className="space-y-1 text-sm">
                  {linkedJhas.map((j) => (
                    <li key={j.id} className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">JHA {j.id}{j.title ? ` — ${j.title}` : ''}</span>
                      {j.signedAt ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-semibold">signed</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">not signed</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="font-bold text-gray-900 border-b pb-2 mb-4">COMPLIANCE STEPS (§6)</h2>
              <ComplianceSteps
                classification={planData.classification}
                liftDetails={planData.liftDetails}
                jhaReference={effectiveJhaReference}
                anyPersonnelLift={planData.liftDetails.some((l) => l.personnelLift)}
              />
            </div>
          </div>
        )}

        {/* S5 (F2) — linked project documents (BOM/CD/RFDS/SCOPE), auto-linked at creation */}
        {!loading && linkedDocuments.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mt-6" data-testid="linked-documents">
            <h2 className="font-bold text-gray-900 border-b pb-2 mb-4">LINKED PROJECT DOCUMENTS (auto-linked)</h2>
            <ul className="space-y-2">
              {linkedDocuments.map((doc) => (
                <li key={doc.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900 break-all">{doc.originalName}</span>
                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full align-middle">
                      {doc.docType}
                    </span>
                  </div>
                  <button
                    data-testid={`download-linked-${doc.id}`}
                    onClick={() => void handleDownloadLinkedDoc(doc)}
                    className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex-shrink-0"
                  >
                    <Download className="h-4 w-4" /> Download
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!loading && !record && (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">Plan not found.</p>
            <button onClick={() => navigate('/construction-plans')} className="text-blue-600 hover:underline">
              Back to construction plans
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
